import { Card, Text } from '@components/ui';
import type { JobData } from '@features/jobs/jobData';
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import { useTranslation } from 'react-i18next';

import { JobFacts } from './job-field';
import { JobLocationMap } from './job-location-map';

const sectionTitleSx = {
  mb: 1.5,
};

interface JobLocationPanelProps {
  job: JobData;
  mapUrl: string | null;
}

const JobLocationPanel = ({
  job,
  mapUrl,
}: JobLocationPanelProps): JSX.Element => {
  const { t } = useTranslation();

  return (
    <Card sx={{ mb: 2 }}>
      <Text as="h2" variant="subtitle" sx={sectionTitleSx}>
        {t('jobDetail.location')}
      </Text>
      <JobFacts
        items={[
          { label: t('jobDetail.address'), value: job.address, wide: true },
          { label: t('jobDetail.pointOfContact'), value: job.contactName },
          { label: t('jobDetail.contactNumber'), value: job.creatorNumber },
          { label: t('jobDetail.email'), value: job.contactEmail, wide: true },
        ]}
      />
      {mapUrl && (
        <Box
          sx={{
            mt: 2,
            overflow: 'hidden',
            borderRadius: '12px',
            border: 1,
            borderColor: 'divider',
            bgcolor: 'rgb(var(--field))',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: { xs: 'flex-start', sm: 'center' },
              justifyContent: 'space-between',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: { xs: 1, sm: 0 },
              borderBottom: 1,
              borderColor: 'divider',
              px: 2,
              py: 1.5,
            }}
          >
            <Text as="span" variant="overline" tone="muted">
              {t('jobDetail.mapPreview')}
            </Text>
            <Link
              href={mapUrl}
              target="_blank"
              rel="noopener noreferrer"
              underline="hover"
              sx={{ fontWeight: 600 }}
            >
              <Text as="span" variant="label" tone="primary">
                {t('jobDetail.openInMaps')}
              </Text>
            </Link>
          </Box>
          <JobLocationMap
            latitude={job.latitude}
            longitude={job.longitude}
            address={job.address}
          />
        </Box>
      )}
    </Card>
  );
};

export default JobLocationPanel;
